import { Constants } from '../common/Constants';
import { DateTimeUtil } from '../common/DateTimeUtil';
import * as InternalTypes from '../common/InternalTypes';
import { Logger } from '../common/Logger';
import { LoggerTexts } from '../common/LoggerTexts';
import { SettingsCheck } from '../common/SettingsCheck';
import { Util } from '../common/Util';
import { CentralConfig } from '../config/CentralConfig';
import { StorageService } from '../storage/StorageService';

export class TimedPushController {
	private _hasOptedOut = false;
	private readonly _centralConfig: CentralConfig;
	private readonly _storage: StorageService;
	private _timedPushDelayInMinutes: number;
	private _minimumTimeToStartTimerForTimedPushInHrs: number;
	private _nextTimedPushDate: number;
	private readonly _logger: Logger;
	private _handleTimedPush: InternalTypes.TimedPushCallBack;
	private _intervalTimer: number;
	private _isDestroyCalled: boolean;

	constructor(centralConfig: CentralConfig, storage: StorageService) {
		this._isDestroyCalled = false;
		this._centralConfig = centralConfig;
		this._storage = storage;
		this._logger = new Logger(Constants.FILE_TIMED_PUSH_CONTROLLER);
	}

	public init(timedPushCallBack: InternalTypes.TimedPushCallBack) {
		const pushConfig = this._centralConfig.pushConfig;
		if (pushConfig) {
			this._timedPushDelayInMinutes = pushConfig.intervalTimerInMinutes;
			this._minimumTimeToStartTimerForTimedPushInHrs = pushConfig.minimumTimeToStartTimerInHrs;
			const userState = Util.readUserState(this._storage);
			if (userState) {
				this._nextTimedPushDate = userState.timedPushDate;
			}
		}
		if (!Number.isFinite(this._timedPushDelayInMinutes)) {
			this._logger.error(LoggerTexts.TIMED_PUSH_CTRL.INIT_INTERVAL_NOT_VALID);
			return;
		}
		if (!Number.isFinite(this._minimumTimeToStartTimerForTimedPushInHrs)) {
			this._logger.error(LoggerTexts.TIMED_PUSH_CTRL.INIT_MIN_TIME_NOT_VALID);
			return;
		}
		this._handleTimedPush = timedPushCallBack;

		if (this._canTimerStart()) {
			this._start();
		}
	}

	public set hasOptedOut(value: boolean) {
		this._hasOptedOut = value;
		if (!value && !this._intervalTimer) {
			if (this._canTimerStart()) {
				this._start();
			}
		}
	}

	public get hasOptedOut(): boolean {
		return this._hasOptedOut;
	}

	public destroy() {
		this._isDestroyCalled = true;
		if (this._intervalTimer) {
			window.clearTimeout(this._intervalTimer);
		}
	}

	private _canTimerStart(): boolean {
		if (!this.hasOptedOut && this._nextTimedPushDate) {
			if (this._nextTimedPushDate - Date.now() <= DateTimeUtil.getMillisecondsFromHours(this._minimumTimeToStartTimerForTimedPushInHrs)) {
				return true;
			}
		}
		return false;
	}

	private _start() {
		this._runTimer().finally(() => {
			this._start();
		});
	}

	private async _runTimer(): Promise<void> {
		return new Promise((resolve) => {
			this._intervalTimer = window.setTimeout(async () => {
				if (this._handleTimedPush && !this._isDestroyCalled && !this.hasOptedOut && !SettingsCheck.isStandaloneModeActive()) {
					await this._handleTimedPush();
					this._logger.info(LoggerTexts.TIMED_PUSH_CTRL.INFO_PUSH_TRIGGERED);
				}
				resolve();
			}, DateTimeUtil.getMillisecondsFromMinutes(this._timedPushDelayInMinutes));
		});
	}
}
